import React, { Fragment, useCallback, useRef } from 'react';
import { styled, SxProp, useBreakpointIndex } from 'dripsy';
import { View, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';

import { IS_WEB } from '@utils';
import { useDraggableScroll } from '@hooks';
import { BaseText } from '../common/BaseText';
import { Icon } from '../common/Icon';
import { Separator } from '../common/Separator';
import { Touchable, TouchableTypeEnum } from '../common/Touchable';
import {
  FlatList,
  FlatListRefType,
  IFlatListProps,
} from '../common/FlatList';
import { EmptyResultTypeEnum, emptyResult } from './EmptyResultView';

const Container = styled(View)({
  px: [null, null, '5%'],
});

const TitleContainer = styled(View)({
  flexDirection: 'row',
});

const Title = styled(BaseText)({
  color: '$heading800',
  fontWeight: 'semi',
  variant: IS_WEB ? 'text.xxlarge' : 'text.xlarge',
});

const BadgeContainer = styled(View)({
  bg: '$primary300',
  borderRadius: 12,
  paddingHorizontal: '$5',
  paddingVertical: '$4',
});

const BadgeText = styled(BaseText)({
  color: '$heading900',
  variant: 'text.small',
});

const HeaderContainer = styled(View)({
  flexDirection: 'row',
  px: ['$6', '$7', '$0'],
  alignItems: 'center',
  justifyContent: 'space-between',
});

const SeeMoreTouchable = styled(Touchable)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const SeeMoreText = styled(BaseText)({
  color: '$heading800',
  variant: 'text.medium',
  mr: '$5',
});

const EmptyResultContainer = styled(View)({
  height: [238, 266],
  flex: 1,
  alignItems: 'center',
});

const SvgContainer = styled(View)({
  flex: 1,
  ...Platform.select({
    native: {
      aspectRatio: 1,
    },
    default: {},
  }),
});

const EmptyResultTitle = styled(BaseText)({
  color: '$heading800',
  variant: IS_WEB ? 'text.xlarge' : 'text.medium',
  fontWeight: 'semi',
  textAlign: 'center',
});

const EmptyResultDescription = styled(BaseText)({
  color: '$text400',
  variant: IS_WEB ? 'text.medium' : 'text.small',
  textAlign: 'center',
  maxWidth: [300, 420],
});

const contentContainerStyle: SxProp = {
  px: ['$6', '$7', '$0'],
  flexGrow: 1,
};

type TitleType = string | { text: string; badge?: string };

interface IHorizontalListProps<TItem> extends IFlatListProps<TItem> {
  data: TItem[];
  title: TitleType;
  onMorePress(): void;
  emptyResultType?: EmptyResultTypeEnum;
}

export function HorizontalList<TItem>({
  title,
  onMorePress,
  emptyResultType,
  ...props
}: IHorizontalListProps<TItem>) {
  const { t, i18n } = useTranslation();
  const ref = useRef<FlatListRefType>(null);
  const bIndex = useBreakpointIndex();
  const { refs } = useDraggableScroll({ outerRef: ref });

  function TitleComponent() {
    if (typeof title === 'object') {
      return (
        <TitleContainer>
          <Title>{title.text}</Title>
          {typeof title.badge !== 'undefined' && bIndex > 0 && (
            <Fragment>
              <Separator size="$4" vertical />
              <BadgeContainer>
                <BadgeText>{title.badge}</BadgeText>
              </BadgeContainer>
            </Fragment>
          )}
        </TitleContainer>
      );
    }

    return <Title>{title}</Title>;
  }

  const ListEmptyComponent = useCallback(() => {
    if (!emptyResultType) {
      return null;
    }

    const SvgComponent = emptyResult.getSvgByType(emptyResultType);
    const tKeys = emptyResult.getKeyByType(emptyResultType);
    const icon = emptyResult.getIconByType(emptyResultType);

    return (
      <EmptyResultContainer>
        <SvgContainer>
          <SvgComponent width="100%" height="100%" />
        </SvgContainer>
        <Separator size={['$6', '$9']} />
        <View>
          <EmptyResultTitle>{`${t(
            tKeys.titleKey,
          )} ${icon}`}</EmptyResultTitle>
          <Separator size="$4" />
          <EmptyResultDescription>
            {t(tKeys.descriptionKey)}
          </EmptyResultDescription>
        </View>
      </EmptyResultContainer>
    );
  }, [emptyResultType, i18n.language]);

  return (
    <Container>
      <HeaderContainer>
        <TitleComponent />
        <SeeMoreTouchable
          onPress={onMorePress}
          type={TouchableTypeEnum.Opacity}
        >
          <SeeMoreText>{t('home.seeMore')}</SeeMoreText>
          <Icon
            name="small-arrow-right-light"
            size={20}
            color="$heading800"
          />
        </SeeMoreTouchable>
      </HeaderContainer>
      <Separator />
      <FlatList
        listRef={refs}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerSx={contentContainerStyle}
        ListEmptyComponent={ListEmptyComponent}
        {...props}
      />
    </Container>
  );
}
